import {
  parseCanvasRenderError,
  type CanvasRenderError,
} from "@/lib/canvas/render";

/** Route of the opaque-origin iframe that runs agent-authored canvas code. */
export const CANVAS_SANDBOX_PATH = "/canvas-sandbox";
export const CANVAS_FRAME_PATH =
  process.env.NODE_ENV === "development"
    ? CANVAS_SANDBOX_PATH
    : "/canvas-frame/canvas-frame.html";
/** Route [code]smith's headless browser opens to capture a new canvas's graphs for its Slack card. */
export const CANVAS_CAPTURE_PATH = "/canvas-capture";

// The graph-image limits live here, not in graphCapture: _app imports this
// module, so anything it imports by value ships in every page's shared bundle.
/** Matches the backend's cap on a canvas version's Slack card gallery. */
export const MAX_CANVAS_GRAPH_IMAGES = 12;
/** The backend's per-image cap: 12 of them stay under its 32M request limit. */
export const MAX_CANVAS_GRAPH_DATA_URL_LENGTH = 2_500_000;
export const PNG_DATA_URL_PREFIX = "data:image/png;base64,";
const MAX_CANVAS_GRAPH_LABEL_LENGTH = 200;

/** Cut by code point, so an emoji is never split into a lone surrogate the backend's JSON parser rejects. */
export function clampGraphLabel(label: string): string {
  return Array.from(label).slice(0, MAX_CANVAS_GRAPH_LABEL_LENGTH).join("");
}

export type CanvasGraphImage = { data: string; label: string | null };

// Standalone pages and print layouts size the iframe to its content;
// workspace panes keep a fixed-height frame with native scrolling.
export const CANVAS_FRAME_LIMITS = {
  DEFAULT_HEIGHT: 320,
  MIN_HEIGHT: 48,
  MAX_CONTENT_HEIGHT: 262_144,
  /** Sandbox boot + compile normally lands well under a second; this is the give-up point. */
  RENDER_TIMEOUT_MS: 15_000,
  /** Longer snapshots are dropped, so a huge canvas re-boots its preview instead of pinning memory. */
  MAX_SNAPSHOT_LENGTH: 1_000_000,
} as const;

/** A content-sized frame's document height, with the frame viewport it was measured in. */
export type CanvasFrameMeasurement = { height: number; viewport: number };

/**
 * A content-sized frame takes the height its document reports, and
 * viewport-relative content (`h-screen`, `100vh`) then grows by that same
 * amount: body 368 → 416 as the viewport goes 320 → 368. That measurement is
 * the frame's own resize echoed back, not new content, and reporting it
 * would grow the frame without end.
 */
export function isFrameViewportEcho(
  previous: CanvasFrameMeasurement,
  next: CanvasFrameMeasurement,
): boolean {
  const viewportDelta = next.viewport - previous.viewport;
  if (viewportDelta === 0) return false;
  return Math.abs(next.height - previous.height - viewportDelta) < 1;
}

export const CANVAS_MESSAGE = {
  READY: "canvas:ready",
  RENDERED: "canvas:rendered",
  ERROR: "canvas:error",
  HEIGHT: "canvas:height",
  RENDER: "canvas:render",
  SCROLL: "canvas:scroll",
  SCROLL_TO: "canvas:scroll-to",
  TOC_SECTIONS: "canvas:toc-sections",
  PRINT: "canvas:print",
  ESCAPE: "canvas:escape",
  SELECTION: "canvas:selection",
  ADD_SELECTION: "canvas:add-selection",
  CLEAR_SELECTION: "canvas:clear-selection",
  SELECTION_ENABLED: "canvas:selection-enabled",
  SUSPENDED: "canvas:suspended",
  OPEN_LINK: "canvas:open-link",
  SNAPSHOT: "canvas:snapshot",
  FIX_ELEMENT: "canvas:fix-element",
  FIX_ELEMENT_RESULT: "canvas:fix-element-result",
  FIX_ELEMENTS_ENABLED: "canvas:fix-elements-enabled",
  GRAPH_IMAGES: "canvas:graph-images",
  GRAPH_IMAGES_ENABLED: "canvas:graph-images-enabled",
} as const;

export type CanvasTheme = "light" | "dark";

export function toCanvasTheme(resolvedTheme: unknown): CanvasTheme {
  return resolvedTheme === "dark" ? "dark" : "light";
}

/** Ties a message to one host render so stale frames cannot affect a newer one. */
type Stamped<T> = T & { generation: number };

/** Set while the host prepares a print: the frame lays out for paper (see `@/lib/canvas/paginate`). */
export type CanvasPrintLayout = { pageHeight: number };

export type CanvasRenderMessage = Stamped<{
  type: typeof CANVAS_MESSAGE.RENDER;
  source: string;
  theme: CanvasTheme;
  print?: CanvasPrintLayout;
  fitContent?: boolean;
  /** The host runs its own print flow, so the frame relays Cmd/Ctrl+P instead of letting the browser print. */
  relayPrintShortcut?: boolean;
  /** False hides `Toc` (canvasMinimapToc experiment off); absent means on. */
  minimapToc?: false;
  hostToc?: true;
  /** Host-side experiment flag; the frame's opaque origin cannot read storage. */
  calloutCodey?: boolean;
  /** Each SDK element fails on its own tile (codesmith_canvas_refresh_enabled); absent, one element's error fails the whole canvas. */
  elementBoundaries?: true;
  /** The frame posts CANVAS_MESSAGE.SNAPSHOT copies of the render once it settles. */
  snapshot?: true;
}>;

/** One `Section` as the frame's `Toc` reports it: `top` px below the frame's top. */
export type CanvasTocSection = { title: string; top: number };

/** Wheel over the host gutter around the frame; the frame scrolls itself by it. */
export type CanvasScrollMessage = {
  type: typeof CANVAS_MESSAGE.SCROLL;
  deltaX: number;
  deltaY: number;
};

export type CanvasScrollToMessage = {
  type: typeof CANVAS_MESSAGE.SCROLL_TO;
  top: number;
};

/** The host took the selection (Add to chat); the frame drops its highlight. */
export type CanvasClearSelectionMessage = {
  type: typeof CANVAS_MESSAGE.CLEAR_SELECTION;
};

/**
 * Whether the frame reports selections and relays Enter/Escape over them.
 * Separate from RENDER so the host can toggle it (e.g. the composer stops
 * accepting quotes) without re-executing the document.
 */
export type CanvasSelectionEnabledMessage = {
  type: typeof CANVAS_MESSAGE.SELECTION_ENABLED;
  enabled: boolean;
};

export type CanvasSuspendedMessage = {
  type: typeof CANVAS_MESSAGE.SUSPENDED;
  suspended: boolean;
};

/**
 * Whether the host can ask the agent to fix an element that failed to
 * render, so the frame offers the button. Separate from RENDER for the same
 * reason as SELECTION_ENABLED: the session losing writes must not re-execute
 * the document.
 */
export type CanvasFixElementsEnabledMessage = {
  type: typeof CANVAS_MESSAGE.FIX_ELEMENTS_ENABLED;
  enabled: boolean;
};

/**
 * Whether the frame posts CANVAS_MESSAGE.GRAPH_IMAGES once a render settles.
 * Separate from RENDER so a workspace pane coming into view starts capture
 * without re-executing the document.
 */
export type CanvasGraphImagesEnabledMessage = {
  type: typeof CANVAS_MESSAGE.GRAPH_IMAGES_ENABLED;
  enabled: boolean;
};

/** Whether the FIX_ELEMENT request with `requestId` reached the session; false re-enables its button. */
export type CanvasFixElementResultMessage = {
  type: typeof CANVAS_MESSAGE.FIX_ELEMENT_RESULT;
  requestId: number;
  sent: boolean;
};

export type CanvasHostMessage =
  | CanvasRenderMessage
  | CanvasScrollMessage
  | CanvasScrollToMessage
  | CanvasClearSelectionMessage
  | CanvasSelectionEnabledMessage
  | CanvasSuspendedMessage
  | CanvasFixElementsEnabledMessage
  | CanvasGraphImagesEnabledMessage
  | CanvasFixElementResultMessage;

/**
 * Text the user selected inside the frame. `sections` names the outermost
 * canvas blocks (cards, tables, charts, code) the selection touches, empty
 * for plain prose; `whole` when it spans every block. The edges are the
 * first and last selection rects in frame viewport pixels.
 */
export type CanvasSelection = {
  text: string;
  sections: string[];
  whole: boolean;
  isCode: boolean;
  top: number;
  bottom: number;
  /** Horizontal center of the first selected line. */
  left: number;
};

/** One SDK element that threw while rendering, e.g. `<BarChart title="Build minutes">`. */
export type CanvasElementFailure = { element: string; message: string };

const MAX_FAILURE_ELEMENT_LENGTH = 200;
const MAX_FAILURE_MESSAGE_LENGTH = 2_000;

export type CanvasFrameMessage =
  | { type: typeof CANVAS_MESSAGE.READY }
  | Stamped<{ type: typeof CANVAS_MESSAGE.RENDERED }>
  | Stamped<{ type: typeof CANVAS_MESSAGE.ERROR } & CanvasRenderError>
  | Stamped<{ type: typeof CANVAS_MESSAGE.HEIGHT; height: number }>
  /** Cmd/Ctrl+P pressed with focus in the frame; the host runs its print flow. */
  | Stamped<{ type: typeof CANVAS_MESSAGE.PRINT }>
  /** Escape pressed with focus in the frame and nothing in it claimed the key. */
  | Stamped<{ type: typeof CANVAS_MESSAGE.ESCAPE }>
  /** A Toc click in a content-sized frame; the host scrolls to `top` px below the frame's top. */
  | Stamped<{ type: typeof CANVAS_MESSAGE.SCROLL_TO; top: number }>
  /** The Toc's sections; the host draws the floating rail from them. */
  | Stamped<{
      type: typeof CANVAS_MESSAGE.TOC_SECTIONS;
      sections: CanvasTocSection[];
      activeIndex?: number;
    }>
  | Stamped<{
      type: typeof CANVAS_MESSAGE.SELECTION;
      selection: CanvasSelection | null;
    }>
  | Stamped<{ type: typeof CANVAS_MESSAGE.ADD_SELECTION }>
  /** A link clicked in the frame, which cannot open popups; the host opens it. */
  | Stamped<{ type: typeof CANVAS_MESSAGE.OPEN_LINK; url: string }>
  /** Static markup of the settled render (see `@/lib/canvas/snapshot`); null withdraws an earlier one. */
  | Stamped<{ type: typeof CANVAS_MESSAGE.SNAPSHOT; html: string | null }>
  /** PNGs of the settled render's charts and diagrams (see `@/lib/canvas/graphCapture`). */
  | Stamped<{
      type: typeof CANVAS_MESSAGE.GRAPH_IMAGES;
      images: CanvasGraphImage[];
    }>
  /** The Fix button on an element that failed to render; the host answers with FIX_ELEMENT_RESULT. */
  | Stamped<
      {
        type: typeof CANVAS_MESSAGE.FIX_ELEMENT;
        requestId: number;
      } & CanvasElementFailure
    >;

/**
 * The link a click in the frame should relay to the host, or null to leave
 * the click to the browser: download links save from inside the frame,
 * in-page `#section` links scroll it, and a right click opens the context
 * menu.
 */
function linkToRelay(event: MouseEvent): Element | null {
  if (event.defaultPrevented || event.button > 1) return null;
  // `a[href]` matches SVG links too, which have no string `href` property.
  const link =
    event.target instanceof Element ? event.target.closest("a[href]") : null;
  const href = link?.getAttribute("href");
  if (!link || href?.startsWith("#") || link.hasAttribute("download")) {
    return null;
  }
  return link;
}

/**
 * The URL to open for `link`, or null for an empty or malformed `href`, which
 * would otherwise reload the frame and drop the canvas.
 */
function linkUrl(link: Element): string | null {
  const href = link.getAttribute("href")?.trim();
  if (!href) return null;
  try {
    return new URL(href, link.baseURI).href;
  } catch {
    return null;
  }
}

/**
 * Cancels each link click `linkToRelay` accepts and calls `open` with its URL,
 * unless a page handler cancels the click itself. Returns a cleanup.
 */
export function relayLinkClicks(
  target: Window,
  open: (url: string) => void,
): () => void {
  const onClick = (event: MouseEvent) => {
    const link = linkToRelay(event);
    if (link === null) return;
    const url = linkUrl(link);
    if (url === null) {
      event.preventDefault();
      return;
    }
    // Capture sees clicks whose page handlers stop propagation, but runs
    // before those handlers: record whether one cancels the click and decide
    // once dispatch has finished.
    const cancel = event.preventDefault.bind(event);
    let handledByPage = false;
    event.preventDefault = () => {
      handledByPage = true;
      cancel();
    };
    cancel();
    setTimeout(() => {
      if (!handledByPage) open(url);
    });
  };
  target.addEventListener("click", onClick, true);
  target.addEventListener("auxclick", onClick, true);
  return () => {
    target.removeEventListener("click", onClick, true);
    target.removeEventListener("auxclick", onClick, true);
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isGeneration(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value > 0;
}

/** More entries than any real canvas has; an untrusted frame cannot flood the host rail. */
const MAX_TOC_SECTIONS = 64;

function parseTocSections(value: unknown): CanvasTocSection[] | null {
  if (!Array.isArray(value) || value.length > MAX_TOC_SECTIONS) return null;
  const sections: CanvasTocSection[] = [];
  for (const item of value) {
    if (
      !isRecord(item) ||
      typeof item.title !== "string" ||
      !isFiniteNumber(item.top)
    ) {
      return null;
    }
    sections.push({ title: item.title, top: item.top });
  }
  return sections;
}

function parseCanvasSelection(value: unknown): CanvasSelection | null {
  if (!isRecord(value)) return null;
  const { text, sections, whole, isCode, top, bottom, left } = value;
  if (
    typeof text !== "string" ||
    !text ||
    typeof isCode !== "boolean" ||
    typeof whole !== "boolean" ||
    !Array.isArray(sections) ||
    !sections.every((name) => typeof name === "string") ||
    !isFiniteNumber(top) ||
    !isFiniteNumber(bottom) ||
    !isFiniteNumber(left)
  ) {
    return null;
  }
  return { text, sections, whole, isCode, top, bottom, left };
}

const OPENABLE_LINK_PROTOCOLS = new Set(["https:", "http:", "mailto:", "tel:"]);

/** Web, email and phone links only: a `javascript:` URL opened by the host would run in the dashboard's origin. */
function parseLinkUrl(value: unknown): string | null {
  if (typeof value !== "string") return null;
  try {
    const url = new URL(value);
    return OPENABLE_LINK_PROTOCOLS.has(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

/** The host uploads these, so it takes only bounded PNG data URIs from the untrusted frame. */
function parseGraphImages(value: unknown): CanvasGraphImage[] | null {
  if (!Array.isArray(value) || value.length > MAX_CANVAS_GRAPH_IMAGES) {
    return null;
  }
  const images: CanvasGraphImage[] = [];
  for (const item of value) {
    if (
      !isRecord(item) ||
      typeof item.data !== "string" ||
      !item.data.startsWith(PNG_DATA_URL_PREFIX) ||
      item.data.length > MAX_CANVAS_GRAPH_DATA_URL_LENGTH ||
      (item.label !== null && typeof item.label !== "string")
    ) {
      return null;
    }
    images.push({
      data: item.data,
      label: item.label === null ? null : clampGraphLabel(item.label),
    });
  }
  return images;
}

export function parseCanvasFrameMessage(
  data: unknown,
): CanvasFrameMessage | null {
  if (!isRecord(data)) return null;
  if (data.type === CANVAS_MESSAGE.READY) return { type: CANVAS_MESSAGE.READY };
  if (!isGeneration(data.generation)) return null;
  const generation = data.generation;

  switch (data.type) {
    case CANVAS_MESSAGE.RENDERED:
      return { type: CANVAS_MESSAGE.RENDERED, generation };
    case CANVAS_MESSAGE.ERROR: {
      const error = parseCanvasRenderError(data);
      return error && { type: CANVAS_MESSAGE.ERROR, generation, ...error };
    }
    case CANVAS_MESSAGE.HEIGHT:
      return isFiniteNumber(data.height)
        ? { type: CANVAS_MESSAGE.HEIGHT, generation, height: data.height }
        : null;
    case CANVAS_MESSAGE.PRINT:
      return { type: CANVAS_MESSAGE.PRINT, generation };
    case CANVAS_MESSAGE.ESCAPE:
      return { type: CANVAS_MESSAGE.ESCAPE, generation };
    case CANVAS_MESSAGE.SCROLL_TO:
      return isFiniteNumber(data.top)
        ? { type: CANVAS_MESSAGE.SCROLL_TO, generation, top: data.top }
        : null;
    case CANVAS_MESSAGE.TOC_SECTIONS: {
      const sections = parseTocSections(data.sections);
      if (!sections) return null;
      if (
        data.activeIndex !== undefined &&
        (!Number.isSafeInteger(data.activeIndex) ||
          (sections.length > 0 &&
            ((data.activeIndex as number) < 0 ||
              (data.activeIndex as number) >= sections.length)))
      )
        return null;
      return {
        type: CANVAS_MESSAGE.TOC_SECTIONS,
        generation,
        sections,
        ...(data.activeIndex !== undefined && {
          activeIndex: data.activeIndex as number,
        }),
      };
    }
    case CANVAS_MESSAGE.SELECTION: {
      if (data.selection === null) {
        return { type: CANVAS_MESSAGE.SELECTION, generation, selection: null };
      }
      const selection = parseCanvasSelection(data.selection);
      return (
        selection && { type: CANVAS_MESSAGE.SELECTION, generation, selection }
      );
    }
    case CANVAS_MESSAGE.ADD_SELECTION:
      return { type: CANVAS_MESSAGE.ADD_SELECTION, generation };
    case CANVAS_MESSAGE.OPEN_LINK: {
      const url = parseLinkUrl(data.url);
      return url === null
        ? null
        : { type: CANVAS_MESSAGE.OPEN_LINK, generation, url };
    }
    case CANVAS_MESSAGE.SNAPSHOT:
      return data.html === null ||
        (typeof data.html === "string" &&
          data.html.length <= CANVAS_FRAME_LIMITS.MAX_SNAPSHOT_LENGTH)
        ? { type: CANVAS_MESSAGE.SNAPSHOT, generation, html: data.html }
        : null;
    case CANVAS_MESSAGE.GRAPH_IMAGES: {
      const images = parseGraphImages(data.images);
      return (
        images && { type: CANVAS_MESSAGE.GRAPH_IMAGES, generation, images }
      );
    }
    case CANVAS_MESSAGE.FIX_ELEMENT:
      return Number.isSafeInteger(data.requestId) &&
        typeof data.element === "string" &&
        data.element &&
        typeof data.message === "string"
        ? {
            type: CANVAS_MESSAGE.FIX_ELEMENT,
            generation,
            requestId: data.requestId as number,
            element: data.element.slice(0, MAX_FAILURE_ELEMENT_LENGTH),
            message: data.message.slice(0, MAX_FAILURE_MESSAGE_LENGTH),
          }
        : null;
    default:
      return null;
  }
}

export function parseCanvasHostMessage(
  data: unknown,
): CanvasHostMessage | null {
  if (!isRecord(data)) return null;
  if (data.type === CANVAS_MESSAGE.CLEAR_SELECTION) {
    return { type: CANVAS_MESSAGE.CLEAR_SELECTION };
  }
  if (data.type === CANVAS_MESSAGE.SELECTION_ENABLED) {
    return typeof data.enabled === "boolean"
      ? { type: CANVAS_MESSAGE.SELECTION_ENABLED, enabled: data.enabled }
      : null;
  }
  if (data.type === CANVAS_MESSAGE.SUSPENDED) {
    return typeof data.suspended === "boolean"
      ? { type: CANVAS_MESSAGE.SUSPENDED, suspended: data.suspended }
      : null;
  }
  if (data.type === CANVAS_MESSAGE.FIX_ELEMENTS_ENABLED) {
    return typeof data.enabled === "boolean"
      ? { type: CANVAS_MESSAGE.FIX_ELEMENTS_ENABLED, enabled: data.enabled }
      : null;
  }
  if (data.type === CANVAS_MESSAGE.GRAPH_IMAGES_ENABLED) {
    return typeof data.enabled === "boolean"
      ? { type: CANVAS_MESSAGE.GRAPH_IMAGES_ENABLED, enabled: data.enabled }
      : null;
  }
  if (data.type === CANVAS_MESSAGE.FIX_ELEMENT_RESULT) {
    return Number.isSafeInteger(data.requestId) &&
      typeof data.sent === "boolean"
      ? {
          type: CANVAS_MESSAGE.FIX_ELEMENT_RESULT,
          requestId: data.requestId as number,
          sent: data.sent,
        }
      : null;
  }
  if (data.type === CANVAS_MESSAGE.SCROLL_TO) {
    return isFiniteNumber(data.top)
      ? { type: CANVAS_MESSAGE.SCROLL_TO, top: data.top }
      : null;
  }
  if (data.type === CANVAS_MESSAGE.SCROLL) {
    return isFiniteNumber(data.deltaX) && isFiniteNumber(data.deltaY)
      ? {
          type: CANVAS_MESSAGE.SCROLL,
          deltaX: data.deltaX,
          deltaY: data.deltaY,
        }
      : null;
  }
  if (
    data.type !== CANVAS_MESSAGE.RENDER ||
    typeof data.source !== "string" ||
    !isGeneration(data.generation)
  ) {
    return null;
  }
  const print =
    isRecord(data.print) &&
    isFiniteNumber(data.print.pageHeight) &&
    data.print.pageHeight > 0
      ? { pageHeight: data.print.pageHeight }
      : undefined;
  return {
    type: CANVAS_MESSAGE.RENDER,
    source: data.source,
    theme: toCanvasTheme(data.theme),
    generation: data.generation,
    ...(print && { print }),
    ...(data.fitContent === true && { fitContent: true }),
    ...(data.relayPrintShortcut === true && { relayPrintShortcut: true }),
    ...(data.minimapToc === false && { minimapToc: false as const }),
    ...(data.hostToc === true && { hostToc: true as const }),
    ...(data.calloutCodey === true && { calloutCodey: true }),
    ...(data.elementBoundaries === true && {
      elementBoundaries: true as const,
    }),
    ...(data.snapshot === true && { snapshot: true as const }),
  };
}
